import { TbCurrencyDollar, TbChartBar, TbStack2, TbChartLine, TbChartDonut, TbChartCandle } from 'react-icons/tb';
import type {
    WidgetConfig,
    WidgetDomain,
    WidgetEditorCatalog,
    MetricCatalogEntry,
    ChartStyle,
    WidgetEditorVariantGroup,
    WidgetFormat,
    WidgetStat,
} from './types';

export const INVENTORY_STORAGE_KEY = 'reporting_inventory_widgets';
export const INVENTORY_LAYOUT_VERSION = 1;

type AggDef = {
    field: string;
    label: string;
    format: WidgetFormat;
    tooltip: string;
    /** Include Total / Avg / Median variants (default true). Derived nets are total-only. */
    withVariants?: boolean;
};

function pascalField(field: string): string {
    return field.charAt(0).toUpperCase() + field.slice(1);
}

function variantMetricValue(field: string, stat: WidgetStat): string {
    if (stat === 'total') return field;
    if (stat === 'average') return `avg${pascalField(field)}`;
    return `median${pascalField(field)}`;
}

function buildAggEntries(def: AggDef): MetricCatalogEntry[] {
    if (def.withVariants === false) {
        return [{
            value: def.field,
            label: def.label,
            format: def.format,
            stat: 'total',
            tooltip: def.tooltip,
        }];
    }
    return ([
        { stat: 'total' as const, suffix: '' },
        { stat: 'average' as const, suffix: ' (Avg.)' },
        { stat: 'median' as const, suffix: ' (Median)' },
    ]).map(({ stat, suffix }) => ({
        value: variantMetricValue(def.field, stat),
        label: `${def.label}${suffix}`,
        format: def.format,
        stat,
        tooltip: def.tooltip,
    }));
}

function buildVariantGroup(def: AggDef): WidgetEditorVariantGroup | null {
    if (def.withVariants === false) return null;
    return {
        label: def.label,
        variants: [
            { value: variantMetricValue(def.field, 'total'), label: 'Total' },
            { value: variantMetricValue(def.field, 'average'), label: 'Avg.' },
            { value: variantMetricValue(def.field, 'median'), label: 'Median' },
        ],
    };
}

/** Base defs for summary cards + simple charts (labels per revise-metrics-prompt). */
const AGG_DEFS: AggDef[] = [
    {
        field: 'inStockValue',
        label: 'In-Stock Value',
        format: 'currency',
        tooltip: 'Total value of items on hand at the end of the period.',
    },
    {
        field: 'inStockQuantity',
        label: 'In-Stock Qty',
        format: 'number',
        tooltip: 'Quantity of items on hand at the end of the period.',
    },
    {
        field: 'orderedQuantity',
        label: 'Ordered Qty',
        format: 'number',
        tooltip: 'Quantity of items ordered in the period.',
    },
    {
        field: 'usedQuantity',
        label: 'Used Qty',
        format: 'number',
        tooltip: 'Quantity of items used in the period.',
    },
    {
        field: 'salesValue',
        label: 'Sales Value',
        format: 'currency',
        tooltip: 'Total sales value in the period at inventory cost.',
    },
    {
        field: 'soldQuantity',
        label: 'Sold Qty',
        format: 'number',
        tooltip: 'Quantity of items sold in the period.',
    },
    {
        field: 'orderedValue',
        label: 'Ordered Value',
        format: 'currency',
        tooltip: 'Total order value in the period at inventory cost.',
    },
    {
        field: 'initialQuantity',
        label: 'Initial Qty',
        format: 'number',
        tooltip: 'Quantity of items added as initial stock in the period.',
    },
    {
        field: 'initialValue',
        label: 'Initial Value',
        format: 'currency',
        tooltip: 'Total value of initial stock added in the period.',
    },
    {
        field: 'usedValue',
        label: 'Used Value',
        format: 'currency',
        tooltip: 'Total value of items used in the period at inventory cost.',
    },
    {
        field: 'zeroCostStockQuantity',
        label: 'Zero-Cost Stock Qty',
        format: 'number',
        tooltip: 'Quantity of on-hand items with zero cost at period end.',
    },
    {
        field: 'inboundQuantity',
        label: 'Inbound Qty',
        format: 'number',
        tooltip: 'Quantity of items added to stock in the period.',
    },
    {
        field: 'inboundValue',
        label: 'Inbound Value',
        format: 'currency',
        tooltip: 'Total value of stock added in the period.',
    },
    {
        field: 'outboundQuantity',
        label: 'Outbound Qty',
        format: 'number',
        tooltip: 'Quantity of items leaving stock in the period.',
    },
    {
        field: 'outboundValue',
        label: 'Outbound Value',
        format: 'currency',
        tooltip: 'Total value of stock leaving in the period.',
    },
];

const DERIVED_DEFS: AggDef[] = [
    {
        field: 'netQuantityChange',
        label: 'Net In-Stock Qty',
        format: 'number',
        tooltip: 'Net change in on-hand quantity over the period.',
        withVariants: false,
    },
    {
        field: 'netTransactionValue',
        label: 'Net Transaction Value',
        format: 'currency',
        tooltip: 'Net change in value of items on hand over the period.',
        withVariants: false,
    },
];

const INVENTORY_SUMMARY_METRICS: MetricCatalogEntry[] = [
    ...AGG_DEFS.flatMap(buildAggEntries),
    ...DERIVED_DEFS.flatMap(buildAggEntries),
];

const COMPOSITE_CHART_ENTRIES: MetricCatalogEntry[] = [
    {
        value: 'quantityFlow',
        label: 'Quantity Flow',
        format: 'number',
        stat: 'total',
        tooltip: 'Inbound and outbound stock activity over the period, with net change.',
    },
    {
        value: 'activityByBusinessLine',
        label: 'Activity by Business Line',
        format: 'number',
        stat: 'total',
        tooltip: 'Share of inventory activity by business line.',
    },
    {
        value: 'orderedVsUsedVsSold',
        label: 'Ordered vs Used vs Sold',
        format: 'number',
        stat: 'total',
        tooltip: 'Ordered, used, and sold quantities over the period.',
    },
    {
        value: 'activityByCategory',
        label: 'Activity by Category',
        format: 'number',
        stat: 'total',
        tooltip: 'Share of inventory activity by part, material, and liquid.',
    },
    {
        value: 'activityByLocation',
        label: 'Activity by Location',
        format: 'number',
        stat: 'total',
        tooltip: 'Inventory activity by location over the period.',
    },
];

/** Chart metrics: composites + per-metric series (incl. avg/median variants). */
const INVENTORY_CHART_METRICS: MetricCatalogEntry[] = [
    ...COMPOSITE_CHART_ENTRIES,
    ...AGG_DEFS.flatMap(buildAggEntries),
    ...DERIVED_DEFS.flatMap(buildAggEntries),
];

const INVENTORY_VARIANT_GROUPS: WidgetEditorVariantGroup[] = AGG_DEFS
    .map(buildVariantGroup)
    .filter((g): g is WidgetEditorVariantGroup => g != null);

const DEFAULT_SUMMARY_METRICS = [
    'inStockValue',
    'netQuantityChange',
    'inStockQuantity',
    'avgOrderedValue',
] as const;

/** Add-widget extras that are not on the default strip (totals only as entry points). */
const ADD_WIDGET_SUMMARY_METRICS = new Set([
    'orderedQuantity',
    'usedQuantity',
    'salesValue',
    'netTransactionValue',
    'soldQuantity',
    'zeroCostStockQuantity',
    'inboundQuantity',
    'inboundValue',
    'outboundQuantity',
    'outboundValue',
    'orderedValue',
    'initialQuantity',
    'initialValue',
    'usedValue',
]);

/** Metrics where an increase is bad (shown red on summary change). */
export const NEGATIVE_INVENTORY_METRICS = new Set(['avgOrderedValue']);

/** Metrics whose comparison change is always neutral gray. */
export const NEUTRAL_CHANGE_INVENTORY_METRICS = new Set([
    'usedQuantity',
    'avgUsedQuantity',
    'medianUsedQuantity',
    'usedValue',
    'avgUsedValue',
    'medianUsedValue',
]);

/** Metrics whose primary value is colored by sign (green/red). */
export const SIGNED_VALUE_INVENTORY_METRICS = new Set([
    'netQuantityChange',
    'netTransactionValue',
]);

const COMPOSITE_CHART_METRICS = new Set(COMPOSITE_CHART_ENTRIES.map((e) => e.value));

function inventoryCatalogEntry(metric: string): MetricCatalogEntry | undefined {
    return [...INVENTORY_SUMMARY_METRICS, ...INVENTORY_CHART_METRICS].find((m) => m.value === metric);
}

function syncInventoryWidgetLabels(widget: WidgetConfig): WidgetConfig {
    const entry = inventoryCatalogEntry(widget.metric);
    if (!entry) return widget;
    return {
        ...widget,
        title: entry.label,
        description: entry.tooltip,
    };
}

const INVENTORY_DOMAINS: { value: WidgetDomain; label: string }[] = [
    { value: 'inventoryTotal', label: 'Total' },
    { value: 'inventoryBusinessLine', label: 'Business Line' },
    { value: 'inventoryCategory', label: 'Category' },
    { value: 'inventoryLocation', label: 'Location' },
];

function generateId() {
    return `invmetric-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildKeyMetric(metricValue: string): WidgetConfig {
    const entry = INVENTORY_SUMMARY_METRICS.find((m) => m.value === metricValue)!;
    return {
        id: generateId(),
        title: entry.label,
        description: entry.tooltip,
        dashboard: 'inventory',
        widgetType: 'keyMetric',
        domain: 'inventoryTotal',
        metric: entry.value,
        stat: entry.stat,
        format: entry.format,
        dataSource: 'summary',
        gridSpan: 1,
        comparison: true,
    };
}

function buildChart(
    metricValue: string,
    chartStyle: ChartStyle,
    gridSpan = 2,
    extras: Partial<WidgetConfig> = {},
): WidgetConfig {
    const entry = INVENTORY_CHART_METRICS.find((m) => m.value === metricValue)!;
    return {
        id: generateId(),
        title: entry.label,
        description: entry.tooltip,
        dashboard: 'inventory',
        widgetType: 'timeSeries',
        domain: 'inventoryTotal',
        metric: entry.value,
        stat: entry.stat,
        format: entry.format,
        dataSource: 'timeseries',
        gridSpan,
        comparison: true,
        chartStyle,
        ...extras,
    };
}

export function getDefaultInventoryWidgets(): WidgetConfig[] {
    return [
        ...DEFAULT_SUMMARY_METRICS.map((m) => buildKeyMetric(m)),
        buildChart('quantityFlow', 'stacked', 4, { showComparisonBars: false }),
        buildChart('inStockValue', 'bars', 2, { showComparisonBars: false }),
        buildChart('netQuantityChange', 'bars', 2, { showComparisonBars: true }),
        buildChart('inStockQuantity', 'line', 2, {
            showComparisonBars: true,
            restockItemsOnly: false,
        }),
        buildChart('netTransactionValue', 'bars', 2, { showComparisonBars: true }),
        buildChart('activityByBusinessLine', 'donut', 2, { showComparisonBars: false }),
    ];
}

/** Map legacy metric keys onto current API / catalog values. */
function migrateLegacyInventoryMetric(metric: string): string {
    if (metric === 'averageOrderCost') return 'avgOrderedValue';
    if (metric === 'totalInventoryValue') return 'inStockValue';
    if (metric === 'avgTotalInventoryValue') return 'avgInStockValue';
    if (metric === 'medianTotalInventoryValue') return 'medianInStockValue';
    if (metric === 'totalSales') return 'salesValue';
    if (metric === 'avgTotalSales') return 'avgSalesValue';
    if (metric === 'medianTotalSales') return 'medianSalesValue';
    return metric;
}

function isKnownInventoryMetric(metric: string, list: MetricCatalogEntry[]): boolean {
    return list.some((m) => m.value === metric);
}

function baseFieldForRestock(metric: string): string {
    if (metric === 'inStockQuantity' || metric === 'avgInStockQuantity' || metric === 'medianInStockQuantity') {
        return 'inStockQuantity';
    }
    return metric;
}

/** Donuts only support activity segment breakdowns (not scalar stock metrics). */
export function inventoryMetricAllowsDonut(metric: string): boolean {
    return metric === 'activityByBusinessLine' || metric === 'activityByCategory';
}

export function sanitizeInventoryWidget(widget: WidgetConfig): WidgetConfig {
    let next: WidgetConfig = {
        ...widget,
        dashboard: 'inventory',
        domain: widget.domain?.startsWith('inventory')
            ? widget.domain
            : 'inventoryTotal',
        metric: migrateLegacyInventoryMetric(widget.metric),
    };

    if (next.widgetType === 'keyMetric') {
        if (!isKnownInventoryMetric(next.metric, INVENTORY_SUMMARY_METRICS)) {
            next = { ...next, metric: 'inStockValue', format: 'currency', stat: 'total' };
        }
    } else if (!isKnownInventoryMetric(next.metric, INVENTORY_CHART_METRICS)) {
        next = { ...next, metric: 'inStockValue', format: 'currency', stat: 'total', chartStyle: 'bars' };
    }

    const entry = inventoryCatalogEntry(next.metric);
    if (entry) {
        next = { ...next, format: entry.format, stat: entry.stat };
    }

    if (next.showComparisonBars === undefined) {
        const defaultOn = new Set([
            'netQuantityChange',
            'netTransactionValue',
            'inStockQuantity',
            'avgInStockQuantity',
            'medianInStockQuantity',
        ]);
        next = {
            ...next,
            showComparisonBars: defaultOn.has(next.metric),
        };
    }

    if (baseFieldForRestock(next.metric) === 'inStockQuantity' && next.restockItemsOnly === undefined) {
        next = { ...next, restockItemsOnly: false };
    }
    if ('showTargetReorderRefs' in next) {
        const { showTargetReorderRefs: _omit, ...stripped } = next as WidgetConfig & {
            showTargetReorderRefs?: boolean;
        };
        next = stripped;
    }

    if (COMPOSITE_CHART_METRICS.has(next.metric)) {
        if (next.metric === 'quantityFlow' && !next.chartStyle) {
            next = { ...next, chartStyle: 'stacked' };
        }
        if (
            (next.metric === 'activityByBusinessLine' || next.metric === 'activityByCategory')
            && next.chartStyle !== 'donut'
        ) {
            next = { ...next, chartStyle: 'donut' };
        }
        if (next.metric === 'activityByLocation' && next.chartStyle !== 'stacked') {
            next = { ...next, chartStyle: 'stacked' };
        }
        if (next.metric === 'orderedVsUsedVsSold' && next.chartStyle !== 'line') {
            next = { ...next, chartStyle: 'line' };
        }
    }

    if (next.chartStyle === 'donut' && !inventoryMetricAllowsDonut(next.metric)) {
        next = { ...next, chartStyle: 'bars' };
    }

    return syncInventoryWidgetLabels(next);
}

const CHART_STYLE_CARDS: { value: ChartStyle; label: string; icon: React.ElementType }[] = [
    { value: 'bars', label: 'Time Series Bar', icon: TbChartCandle as React.ElementType },
    { value: 'stacked', label: 'Stacked Bar', icon: TbStack2 as React.ElementType },
    { value: 'line', label: 'Line Chart', icon: TbChartLine as React.ElementType },
    { value: 'donut', label: 'Donut Chart', icon: TbChartDonut as React.ElementType },
    { value: 'histogram', label: 'Bar Chart', icon: TbChartBar as React.ElementType },
];

const INVENTORY_WIDGET_TYPES: { value: string; label: string; icon: React.ElementType }[] = [
    { value: 'keyMetric', label: 'Summary Card', icon: TbCurrencyDollar as React.ElementType },
    ...CHART_STYLE_CARDS,
];

function inventoryShowPriorBars(config: WidgetConfig): boolean {
    if (config.widgetType !== 'timeSeries') return false;
    if (config.metric === 'quantityFlow') return true;
    const style = config.chartStyle ?? 'bars';
    return style === 'bars' || style === 'line' || style === 'histogram' || style === 'stacked';
}

export const INVENTORY_CATALOG: WidgetEditorCatalog = {
    widgetTypes: INVENTORY_WIDGET_TYPES,
    widgetTypesFor: (config) => {
        if (config.metric === 'quantityFlow') {
            return INVENTORY_WIDGET_TYPES.filter((t) => t.value === 'keyMetric' || t.value === 'stacked');
        }
        if (config.metric === 'activityByBusinessLine' || config.metric === 'activityByCategory') {
            return INVENTORY_WIDGET_TYPES.filter((t) => t.value === 'keyMetric' || t.value === 'donut');
        }
        if (config.metric === 'activityByLocation') {
            return INVENTORY_WIDGET_TYPES.filter((t) => t.value === 'keyMetric' || t.value === 'stacked');
        }
        if (config.metric === 'orderedVsUsedVsSold') {
            return INVENTORY_WIDGET_TYPES.filter((t) => t.value === 'keyMetric' || t.value === 'line');
        }
        if (config.widgetType !== 'keyMetric' && !inventoryMetricAllowsDonut(config.metric)) {
            return INVENTORY_WIDGET_TYPES.filter((t) => t.value !== 'donut');
        }
        return INVENTORY_WIDGET_TYPES;
    },
    domains: INVENTORY_DOMAINS.filter((d) => d.value === 'inventoryTotal'),
    metricsFor: (config) => {
        if (config.widgetType === 'keyMetric') {
            return INVENTORY_SUMMARY_METRICS;
        }
        if (config.chartStyle === 'donut') {
            return INVENTORY_CHART_METRICS.filter((m) => inventoryMetricAllowsDonut(m.value));
        }
        return INVENTORY_CHART_METRICS;
    },
    variantGroups: INVENTORY_VARIANT_GROUPS,
    selectedCardId: (config) =>
        config.widgetType === 'keyMetric' ? 'keyMetric' : (config.chartStyle ?? 'bars'),
    applyCardId: (cardId, config) => {
        if (cardId === 'keyMetric') {
            return {
                widgetType: 'keyMetric',
                dataSource: 'summary',
                gridSpan: 1,
                chartStyle: undefined,
                metric: 'inStockValue',
                format: 'currency',
                stat: 'total',
                domain: 'inventoryTotal',
            };
        }
        if (cardId === 'donut' && !inventoryMetricAllowsDonut(config.metric)) {
            const fallback = INVENTORY_CHART_METRICS.find((m) => m.value === 'activityByBusinessLine')!;
            return {
                widgetType: 'timeSeries',
                dataSource: 'timeseries',
                gridSpan: 2,
                chartStyle: 'donut',
                metric: fallback.value,
                format: fallback.format,
                stat: fallback.stat,
                domain: 'inventoryTotal',
            };
        }
        const chartStyle = cardId as ChartStyle;
        const defaultMetric =
            chartStyle === 'donut'
                ? 'activityByBusinessLine'
                : chartStyle === 'stacked'
                    ? 'quantityFlow'
                    : chartStyle === 'line'
                        ? 'inStockQuantity'
                        : 'inStockValue';
        const entry = INVENTORY_CHART_METRICS.find((m) => m.value === defaultMetric)!;
        return {
            widgetType: 'timeSeries',
            dataSource: 'timeseries',
            gridSpan: 2,
            chartStyle,
            metric: entry.value,
            format: entry.format,
            stat: entry.stat,
            domain: 'inventoryTotal',
        };
    },
    applyMetric: (metric, config) => {
        const entry = inventoryCatalogEntry(metric);
        if (!entry) return {};
        const patch: Partial<WidgetConfig> = {
            metric,
            format: entry.format,
            stat: entry.stat,
        };
        if (metric === 'quantityFlow') patch.chartStyle = 'stacked';
        if (metric === 'activityByBusinessLine' || metric === 'activityByCategory') {
            patch.chartStyle = 'donut';
        }
        if (metric === 'activityByLocation') patch.chartStyle = 'stacked';
        if (metric === 'orderedVsUsedVsSold') patch.chartStyle = 'line';
        if (
            config.chartStyle === 'donut'
            && !inventoryMetricAllowsDonut(metric)
            && !patch.chartStyle
        ) {
            patch.chartStyle = 'bars';
        }
        if (baseFieldForRestock(metric) === 'inStockQuantity' && patch.restockItemsOnly === undefined) {
            patch.restockItemsOnly = false;
        }
        return patch;
    },
    showPriorFor: inventoryShowPriorBars,
    showSortFor: () => false,
    showLocationLimitFor: () => false,
    showRestockItemsOnlyFor: (config) =>
        config.widgetType === 'timeSeries'
        && baseFieldForRestock(config.metric) === 'inStockQuantity',
    defaultConfig: () => buildChart('inStockValue', 'bars'),
};

export {
    INVENTORY_SUMMARY_METRICS,
    INVENTORY_CHART_METRICS,
    ADD_WIDGET_SUMMARY_METRICS,
    COMPOSITE_CHART_METRICS,
    INVENTORY_VARIANT_GROUPS,
    variantMetricValue,
};
